import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { invalidateImageLibraryQuery } from '../ImageLibrary/useImageLibraryQuery';

export function useImageAttachmentMutation({
	onError,
	onSuccess,
}: {
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: (result: Api.IFileAttachment[]) => void;
} = {}) {
	const userSession = useUserSession();
	return useMutation({
		mutationFn: async ({
			files,
			resizeForSocialMedia = false,
			resizeForNewsletter = false,
			addToLibrary = false,
			impersonationContext,
		}: {
			files: File[];
			resizeForSocialMedia?: boolean;
			/** Caps the image at 1024px for HTML newsletters, matching the Unlayer upload path */
			resizeForNewsletter?: boolean;
			/** Set by the content surfaces so the upload also joins the account's reusable image library */
			addToLibrary?: boolean;
			impersonationContext?: Api.IImpersonationContext;
		}) => {
			const formData = new FormData();
			files.forEach(file => formData.append('files', file));
			return new Promise<Api.IFileAttachment[]>((resolve, reject) => {
				const onFinish = (opResult: Api.IBulkOperationResult<Api.IFileAttachment>) => {
					if (opResult.success) {
						return resolve(opResult.succeeded);
					}
					reject(opResult);
				};
				userSession.webServiceHelper.callWebServiceWithBulkOperationResult<Api.IFileAttachment>(
					Api.ImpersonationBroker.composeApiUrl({
						queryParams: { resizeForSocialMedia, resizeForNewsletter, addToLibrary },
						urlPath: `attachment/image`,
						impersonationContext,
					}),
					'POST',
					formData,
					onFinish,
					onFinish
				);
			});
		},
		onError,
		// The upload itself is what creates the library entry when addToLibrary is set, so the library
		// listing has to be refreshed here rather than by the add mutation, which this path never calls.
		onSuccess: (result, variables) => {
			if (variables.addToLibrary) {
				invalidateImageLibraryQuery();
			}
			onSuccess?.(result);
		},
	});
}
